import {
  Badge,
  Button,
  Dialog,
  Icon,
  IconSizeClasses,
} from '@eppendorf/vnls-react-components';
import { PropsWithChildren, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import {
  helpEmail,
  subscriptionNavigationPath,
} from '$components/subscription-dialog/subscription-dialog';

import './report-hub-upsell-nav-item.scss';

interface ReportBenefitProps {
  icon: 'audit' | 'records' | 'charts';
  translationKey: 'auditTrailExport' | 'runRecords' | 'stabilityReports';
}

function ReportBenefit({ icon, translationKey }: ReportBenefitProps) {
  return (
    <div className="flex gap-l flex__ai--center">
      <Icon name={icon} size={IconSizeClasses.Small} />
      <p>
        <Trans
          i18nKey={`subscription.reportHubUpgrade.${translationKey}`}
          components={{ bold: <span className="font-weight-bold" /> }}
        />
      </p>
    </div>
  );
}

function SupportLink({ children }: PropsWithChildren) {
  return (
    <a href={`mailto:${helpEmail}`} className="color-blue-500">
      {children}
    </a>
  );
}

interface ReportHubUpsellDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ReportHubBetaAccessDialog({
  isOpen,
  onOpenChange,
}: ReportHubUpsellDialogProps) {
  const { t } = useTranslation();

  return (
    <Dialog
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      hideCloseButton
      className="w-max-4xl"
    >
      <div className="flex flex__ai--center gap-m m-bottom-xxl">
        <Dialog.Title className="m-bottom-none">
          {t('subscription.reportHubUpgrade.title')}
        </Dialog.Title>
        <Badge color="purple" size="small" className="bg-purple-500 color-white">
          <span className="flex flex__ai--center gap-xxs">
            <Icon name="flag" size={IconSizeClasses.XXSmall} className="bg-white" />
            {t('subscription.reportHubBetaAccess.badge')}
          </span>
        </Badge>
      </div>
      <Dialog.Description />

      <div className="flex flex__dir--column gap-xxl font-size-m">
        <p>
          <Trans
            i18nKey="subscription.reportHubUpgrade.description"
            components={{ bold: <span className="font-weight-bold" /> }}
          />
        </p>

        <div className="flex flex__dir--column gap-l">
          <ReportBenefit icon="audit" translationKey="auditTrailExport" />
          <ReportBenefit icon="records" translationKey="runRecords" />
          <ReportBenefit icon="charts" translationKey="stabilityReports" />
        </div>

        <div className="border-solid border-s border-color-purple-500 border-radius-l p-l bg-gray-50">
          <p>
            <Trans
              i18nKey="subscription.reportHubBetaAccess.notice"
              components={{ bold: <span className="font-weight-bold" /> }}
            />
          </p>
        </div>
      </div>

      <Dialog.Actions>
        <Button variant="secondary" onClick={() => onOpenChange(false)}>
          {t('shared.close')}
        </Button>
        <Button variant="primary">
          <a
            href="mailto:visionize.support@eppendorf.com?subject=Report%20Hub%20beta%20access&body=Hello%2C%0A%0AI%20would%20like%20access%20to%20the%20Report%20Hub%20beta.%0A%0AThank%20you."
            className="flex flex__ai--center"
          >
            {t('subscription.reportHubBetaAccess.button')}
            <Icon
              name="external-link"
              size={IconSizeClasses.XSmall}
              className="m-left-s bg-white"
            />
          </a>
        </Button>
      </Dialog.Actions>
    </Dialog>
  );
}

export function ReportHubUpsellDialog({
  isOpen,
  onOpenChange,
}: ReportHubUpsellDialogProps) {
  const { t } = useTranslation();

  return (
    <Dialog
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      hideCloseButton
      className="w-max-4xl"
    >
      <div className="flex flex__ai--center gap-m m-bottom-xxl">
        <Dialog.Title className="m-bottom-none">
          {t('subscription.reportHubUpgrade.title')}
        </Dialog.Title>
        <Badge
          color="purple"
          size="small"
          bordered
          className="whitespace-nowrap bg-white font-weight-bold"
        >
          {t('subscription.reportHubUpgrade.badge')}
        </Badge>
      </div>
      <Dialog.Description />

      <div className="flex flex__dir--column gap-xxl font-size-m">
        <p className="font-weight-bold">
          {t('subscription.reportHubUpgrade.description')}
        </p>

        <div className="flex flex__dir--column gap-l">
          <ReportBenefit icon="audit" translationKey="auditTrailExport" />
          <ReportBenefit icon="records" translationKey="runRecords" />
          <ReportBenefit icon="charts" translationKey="stabilityReports" />
        </div>

        <div className="border-solid border-s border-color-purple-500 border-radius-l p-l bg-gray-50">
          <p className="font-weight-bold m-bottom-xs">
            {t('subscription.reportHubUpgrade.beta.title')}
          </p>
          <p>{t('subscription.reportHubUpgrade.beta.description')}</p>
        </div>

        <div className="border-solid border-s border-color-purple-500 border-radius-l p-l gap-l bg-gray-50 flex flex__dir--column">
          <div className="flex gap-s">
            <Icon name="info" size={IconSizeClasses.XSmall} className="bg-purple-500" />
            <p>
              <Trans
                i18nKey="subscription.reportHubUpgrade.gainFullAccess"
                components={{ bold: <span className="font-weight-bold" /> }}
              />
            </p>
          </div>
          <p className="font-size-s color-gray-800 text-align-center">
            <Trans
              i18nKey="subscription.reportHubUpgrade.helpText"
              components={{ l: <SupportLink /> }}
            />
          </p>
        </div>
      </div>

      <Dialog.Actions>
        <Button variant="secondary" onClick={() => onOpenChange(false)}>
          {t('shared.close')}
        </Button>
        <Button variant="primary">
          <a
            href={subscriptionNavigationPath}
            target="_blank"
            rel="noopener noreferrer"
            className="flex flex__ai--center"
          >
            {t('subscription.reportHubUpgrade.button')}
            <Icon
              name="external-link"
              size={IconSizeClasses.XSmall}
              className="m-left-s bg-white"
            />
          </a>
        </Button>
      </Dialog.Actions>
    </Dialog>
  );
}

export function EssentialsReportHubUpsellNavItem() {
  const { t } = useTranslation();
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  return (
    <>
      <li>
        <button
          type="button"
          aria-label={t('sidebar.reportHub')}
          className="sidebar__anchor report-hub-upsell-nav-item"
          onClick={() => setIsDialogOpen(true)}
        >
          <span className="report-hub-upsell-nav-item__label">
            {t('sidebar.reportHub')}
          </span>
          <div className="relative">
            <Icon name="records" className="sidebar__icon" size={IconSizeClasses.Small} />
          </div>
        </button>
      </li>

      <ReportHubUpsellDialog isOpen={isDialogOpen} onOpenChange={setIsDialogOpen} />
    </>
  );
}

export function ReportHubBetaAccessNavItem() {
  const { t } = useTranslation();
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  return (
    <>
      <li>
        <button
          type="button"
          aria-label={t('sidebar.reportHub')}
          className="sidebar__anchor report-hub-upsell-nav-item"
          onClick={() => setIsDialogOpen(true)}
        >
          <span className="report-hub-upsell-nav-item__label">
            {t('sidebar.reportHub')}
          </span>
          <div className="relative">
            <Icon name="records" className="sidebar__icon" size={IconSizeClasses.Small} />
          </div>
        </button>
      </li>

      <ReportHubBetaAccessDialog isOpen={isDialogOpen} onOpenChange={setIsDialogOpen} />
    </>
  );
}
